import { useLocalization } from '@lib/localization';
import { createPdfWorker, loadPdfjs, type OwnPdfWorker } from '@lib/pdf/pdfjs';
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { buttonStyles } from '@pages/CustomPoiTypes.styles';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  barStyles, emptyStyles, failureDetailStyles, failureStyles, frameStyles, outdatedStyles, progressSlotStyles,
  scrollerStyles, sheetStyles,
} from './PdfPreview.styles';
import { type PreviewAnswer } from './previewAnswer';

interface Props {
  /**
   * Asks the server for the PDF as it is to be drawn now. Called with the newest props at
   * the moment of asking, and aborted only when the preview leaves the screen: a request
   * that is on its way is never given up for a newer one, since abandoning it does not stop
   * the server drawing it.
   */
  request: (signal: AbortSignal) => Promise<PreviewAnswer>;
  /**
   * Everything the picture depends on, as one value compared with `Object.is`. The preview
   * asks again whenever it changes.
   */
  revision: unknown;
  /** False while what would be sent cannot be drawn: nothing is asked meanwhile. */
  valid?: boolean;
  /**
   * True while the picture is not of what is on screen although the revision has not moved:
   * a caller that holds the revision still until it is asked to draw (a real inspection,
   * drawn only on Refresh) says so here. The picture is then shown as one that is out of
   * date, and a refusal that belonged to it is not shown at all. Nothing is asked meanwhile
   * either: `request` draws what is on screen at the moment it is called, and the picture
   * would be kept as that of a revision which was made for something else.
   */
  stale?: boolean;
  /**
   * True once there is nothing left to draw: what the preview was of is gone, which the
   * caller says in its own place. Nothing is asked from then on and the last picture stays
   * as it is. Unlike `valid` it has no end to wait for, so nothing here speaks of a pause,
   * or of a picture that is yet to come.
   */
  closed?: boolean;
  /** The caller's controls, at the start of the bar over the pages. */
  toolbar?: React.ReactNode;
}

/** A PDF that has been read and can be drawn a page at a time. */
interface Picture {
  task: PDFDocumentLoadingTask;
  pdf: PDFDocumentProxy;
  url: string;
  pages: number;
  /** Height over width of its first page. Every sheet is laid out by it before it is drawn. */
  aspect: number;
  /** The revision it was asked for with. */
  revision: unknown;
}

interface Failure {
  message: string;
  status?: number;
}

/** How long the author has to pause before a change is asked for. */
const SETTLE_MS = 400;

/** What `answered` holds before anything was asked: equal to no revision a caller can pass. */
const NEVER = Symbol('never asked');

/** A sheet's width in CSS pixels where the browser has not said yet how wide the preview is. */
const DEFAULT_WIDTH = 640;

/** Sheets are redrawn for a new width only in steps, so dragging a window's edge is not a redraw a pixel. */
const WIDTH_STEP = 32;

const UNDRAWABLE = 422;
const ALREADY_DRAWING = 429;

/** Lets go of a picture: its document in the worker, and the address of its bytes. */
const discard = (picture: Picture): Promise<void> => {
  URL.revokeObjectURL(picture.url);

  return picture.task.destroy().catch(() => undefined);
};

interface PageProps {
  picture: Picture;
  number: number;
  inView: boolean;
  /** In CSS pixels. */
  width: number;
  label: string;
  /** Whether a newer picture is on its way or wanted. */
  outdated: boolean;
  /** Has the preview tell when this sheet scrolls into or out of view. Answers how to stop. */
  watch: (element: Element, number: number) => () => void;
}

/**
 * One sheet. It is drawn only while it is in view, and holds no bitmap otherwise: a real
 * inspection's report runs to hundreds of pages.
 */
const PreviewPage: React.FC<PageProps> = ({ picture, number, inView, width, label, outdated, watch }) => {
  const sheet = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => (sheet.current ? watch(sheet.current, number) : undefined), [watch, number]);

  useEffect(() => {
    const target = canvas.current;

    if (!target) return undefined;

    if (!inView) {
      target.width = 0;
      target.height = 0;

      return undefined;
    }

    let cancelled = false;
    let rendering: RenderTask | undefined;

    void (async () => {
      const page = await picture.pdf.getPage(number);

      if (cancelled) return;

      const scale = (width * (window.devicePixelRatio || 1)) / page.getViewport({ scale: 1 }).width;
      const viewport = page.getViewport({ scale });

      // Drawn off screen and copied over in one step: the sheet keeps showing the last
      // picture's page until this one's is whole, instead of going white in between.
      const buffer = document.createElement('canvas');

      buffer.width = Math.floor(viewport.width);
      buffer.height = Math.floor(viewport.height);

      const context = buffer.getContext('2d');

      if (!context) return;

      rendering = page.render({ canvasContext: context, viewport });
      await rendering.promise;

      if (cancelled) return;

      target.width = buffer.width;
      target.height = buffer.height;
      target.getContext('2d')?.drawImage(buffer, 0, 0);
    })().catch(() => {
      // Cancelled below, or the document was replaced under it: the next draw makes up for it.
    });

    return () => {
      cancelled = true;
      rendering?.cancel();
    };
  }, [picture, number, inView, width]);

  return (
    <Box
      ref={sheet}
      sx={outdated ? [sheetStyles, outdatedStyles] : sheetStyles}
      style={{ aspectRatio: `1 / ${picture.aspect}` }}
    >
      <canvas ref={canvas} role="img" aria-label={label} />
    </Box>
  );
};

/**
 * A report as the server draws it, kept up to date with what the caller is editing.
 *
 * One request at a time. A change that arrives while one is on its way marks the picture
 * as out of date, and when the answer comes one more request follows, and only one, for the
 * state of things once the author has paused. Not if the caller says by then that nothing
 * may be asked (`valid` is false, or the picture is `stale`): the request that is owed
 * waits until one may be, and is not sent at all if the revision is back by then at what
 * was last answered, or once the preview is `closed`. The server draws one preview a user
 * at a time as well, and a request that was given up keeps it busy until it is through, so
 * sending each change as it is made would only queue the author behind their own
 * keystrokes.
 *
 * The last picture that could be drawn stays on screen while a newer one is fetched, and
 * when it cannot be: an error is shown over it, not in its place.
 */
const PdfPreview: React.FC<Props> = ({ request, revision, valid = true, stale = false, closed = false, toolbar }) => {
  const { t } = useLocalization();

  const [picture, setPicture] = useState<Picture | null>(null);
  // A request is waiting for the author to pause, or on its way.
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [inView, setInView] = useState<ReadonlySet<number>>(() => new Set());
  const [width, setWidth] = useState(0);

  // Whether a request may be sent now. Not while the caller holds the revision still over a
  // screen that has moved on: a request that was still owed would draw the screen as it is,
  // unasked, and its picture would pass for an up to date one as soon as the screen came
  // back to what the revision was made for. A request held back like this is sent then, or
  // the caller moves the revision. And never again once the caller has closed the preview.
  const askable = valid && !stale && !closed;

  // What the props are now, for a request that is sent later than the render that wanted it.
  const latest = useRef({ request, revision, askable });

  latest.current = { request, revision, askable };

  const flight = useRef({
    asking: false,
    /** The revision the last answer was for, a refusal included. */
    answered: NEVER as unknown,
    /** Rises when the preview leaves the screen, so an answer that comes after it knows. */
    token: 0,
    /** True once the preview has left the screen: nothing is asked for it any more. */
    gone: false,
    timer: undefined as ReturnType<typeof setTimeout> | undefined,
    controller: null as AbortController | null,
  });

  // A worker of the preview's own: destroying a document it loaded leaves the shared one alone.
  const own = useRef<Promise<OwnPdfWorker> | null>(null);
  const disposal = useRef<Promise<void>>(Promise.resolve());

  const scroller = useRef<HTMLDivElement>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const watched = useRef(new Map<Element, number>());

  /**
   * Reads the PDF behind an address into a picture. `wanted` says whether the preview is
   * still on screen: it is asked after every wait, because a worker started for a preview
   * that has left would be nobody's to stop.
   */
  const open = useCallback(async (url: string, asked: unknown, wanted: () => boolean): Promise<Picture> => {
    const pdfjs = await loadPdfjs();

    if (!wanted()) throw new Error('The preview is gone');

    if (!own.current) {
      const starting = createPdfWorker();

      own.current = starting;
      // One that could not start is not kept: the next picture tries again.
      starting.catch(() => {
        if (own.current === starting) own.current = null;
      });
    }

    const { worker } = await own.current;

    if (!wanted()) throw new Error('The preview is gone');

    const task = pdfjs.getDocument({ url, worker });

    try {
      const pdf = await task.promise;
      const first = await pdf.getPage(1);
      const { width: pageWidth, height } = first.getViewport({ scale: 1 });

      return { task, pdf, url, pages: pdf.numPages, aspect: height / pageWidth, revision: asked };
    } catch (error) {
      void task.destroy().catch(() => undefined);

      throw error;
    }
  }, []);

  const ask = useCallback(function ask() {
    const state = flight.current;
    const { request: send, revision: asked, askable: allowed } = latest.current;

    if (state.asking || state.gone || !allowed) return;

    const token = ++state.token;
    const controller = new AbortController();

    state.asking = true;
    state.controller = controller;
    setBusy(true);

    void send(controller.signal)
      .catch((): PreviewAnswer => ({ error: '' }))
      .then(async (answer) => {
        let next: Picture | null = null;
        let failed: Failure | null = null;

        if ('url' in answer) {
          try {
            next = await open(answer.url, asked, () => token === state.token);
          } catch (_) {
            // An answer that is not a PDF pdf.js can read, or one nobody is waiting for.
            URL.revokeObjectURL(answer.url);
            failed = { message: '' };
          }
        } else {
          failed = { message: answer.error, status: answer.status };
        }

        // The preview left the screen while this was being read: its picture is nobody's.
        if (token !== state.token) {
          if (next) void discard(next);

          return;
        }

        if (next) setPicture(next);
        setFailure(failed);

        state.asking = false;
        state.controller = null;
        state.answered = asked;

        // What changed meanwhile is asked for once, however many changes there were, and
        // after the same pause as any change. Asked at once, each answer found the next key
        // already pressed: while the author typed faster than the server drew, previews went
        // out back to back, and a render holds the process everybody's requests wait on.
        const now = latest.current;

        if (now.askable && !Object.is(now.revision, asked)) {
          clearTimeout(state.timer);
          state.timer = setTimeout(ask, SETTLE_MS);
        } else {
          setBusy(false);
        }
      });
  }, [open]);

  useEffect(() => {
    const state = flight.current;

    // The answer that is on its way finds this change and asks once more.
    if (state.asking) return undefined;

    // Whatever was waiting to be asked is asked for the state of things now, or not at all:
    // a change that was taken back sends nothing.
    clearTimeout(state.timer);

    if (!askable || Object.is(revision, state.answered)) {
      setBusy(false);

      return undefined;
    }

    // The first picture is asked for at once. A change waits for the author to pause: a
    // request sent on the first key of a word holds the next one back by a whole drawing.
    setBusy(true);
    state.timer = setTimeout(ask, state.answered === NEVER ? 0 : SETTLE_MS);

    return () => clearTimeout(state.timer);
  }, [revision, askable, ask]);

  // The picture that was replaced is destroyed once the new one is on screen. Declared
  // before the effect below, whose cleanup waits for the last of these.
  useEffect(() => () => {
    if (picture) disposal.current = discard(picture);
  }, [picture]);

  useEffect(() => {
    const state = flight.current;

    state.gone = false;

    return () => {
      state.gone = true;
      state.token += 1;
      state.asking = false;
      clearTimeout(state.timer);
      state.controller?.abort();
      state.controller = null;

      observer.current?.disconnect();
      observer.current = null;

      const worker = own.current;

      own.current = null;

      // Stopped after the last document has let go of it.
      if (worker) {
        void disposal.current.then(() => worker).then((ended) => ended.destroy()).catch(() => undefined);
      }
    };
  }, []);

  useEffect(() => {
    const element = scroller.current;

    if (!element || typeof ResizeObserver === 'undefined') return undefined;

    const resized = new ResizeObserver(([entry]) => {
      setWidth(Math.round(entry.contentRect.width / WIDTH_STEP) * WIDTH_STEP);
    });

    resized.observe(element);

    return () => resized.disconnect();
  }, []);

  const watch = useCallback((element: Element, number: number) => {
    const leave = () => setInView((current) => {
      if (!current.has(number)) return current;

      const next = new Set(current);

      next.delete(number);

      return next;
    });

    // Without the observer every sheet counts as in view, and all of them are drawn.
    if (typeof IntersectionObserver === 'undefined') {
      setInView((current) => new Set(current).add(number));

      return leave;
    }

    observer.current ??= new IntersectionObserver((entries) => {
      setInView((current) => {
        const next = new Set(current);

        for (const entry of entries) {
          const page = watched.current.get(entry.target);

          if (page === undefined) continue;

          if (entry.isIntersecting) next.add(page);
          else next.delete(page);
        }

        return next;
      });
      // Half a screen above and below is drawn ahead, so scrolling does not show blanks.
    }, { root: scroller.current, rootMargin: '50% 0px' });

    watched.current.set(element, number);
    observer.current.observe(element);

    return () => {
      watched.current.delete(element);
      observer.current?.unobserve(element);
      leave();
    };
  }, []);

  const outdated = picture !== null && (stale || !Object.is(picture.revision, revision));

  const headline = (status?: number) => {
    if (status === UNDRAWABLE) return t('ReportPreview_Failed');
    if (status === ALREADY_DRAWING) return t('ReportPreview_Busy');

    return t('ReportPreview_Error');
  };

  return (
    <Box sx={frameStyles}>
      <Stack direction="row" sx={barStyles}>
        {toolbar}
        <Box sx={{ flex: 1 }} />
        {picture && busy && <Typography variant="caption" color="text.secondary">{t('ReportPreview_Updating')}</Typography>}
        {/* Only of a pause that ends: a closed preview has nothing listed anywhere to be fixed. */}
        {picture && !busy && outdated && !valid && !closed && (
          <Typography variant="caption" color="warning.main">{t('ReportPreview_Paused')}</Typography>
        )}
      </Stack>

      <Box sx={progressSlotStyles}>{busy && <LinearProgress sx={{ height: 2 }} />}</Box>

      {failure && !stale && (
        <Stack direction="row" sx={failureStyles}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{headline(failure.status)}</Typography>
            {failure.message && failure.status !== ALREADY_DRAWING && (
              <Typography variant="caption" sx={failureDetailStyles}>{failure.message}</Typography>
            )}
          </Box>
          <Button size="small" color="inherit" sx={buttonStyles} disabled={busy || !valid || closed} onClick={ask}>
            {t('ReportPreview_Retry')}
          </Button>
        </Stack>
      )}

      <Box ref={scroller} sx={scrollerStyles}>
        {picture
          ? Array.from({ length: picture.pages }, (_, index) => (
            <PreviewPage
              key={index}
              picture={picture}
              number={index + 1}
              inView={inView.has(index + 1)}
              width={width || DEFAULT_WIDTH}
              label={t('ReportPreview_Page', [index + 1, picture.pages])}
              outdated={outdated}
              watch={watch}
            />
          ))
          : (!failure || stale) && !closed && (
            <Typography variant="body2" sx={emptyStyles}>
              {t(busy ? 'ReportPreview_Updating' : valid ? 'ReportPreview_Empty' : 'ReportPreview_Paused')}
            </Typography>
          )}
      </Box>
    </Box>
  );
};

export default PdfPreview;
